"use client";

import React, { useState, useMemo, useEffect } from "react";

import { TabItem, TabItems } from "./TabItems";

export type Tab = TabItem & {
  content: React.ReactNode;
};

interface TabBarProps {
  tabs: Tab[];
  initialTab?: string;
  tabControlClassName?: string;
  onTabChange?: (id: string) => Promise<boolean>;
  isShowTicked?: boolean;
}

export const NewTabBar: React.FC<TabBarProps> = ({
  tabs,
  initialTab,
  tabControlClassName,
  onTabChange,
  isShowTicked,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab || tabs[0].id);

  const handleTabClick = async (tab: TabItem) => {
    const { id, isDisabled } = tab;

    if (activeTab === id || isDisabled) return;

    if (onTabChange) {
      const shouldProceed = await onTabChange(id);
      if (shouldProceed) {
        setActiveTab(id);
      }
    } else {
      setActiveTab(id);
    }
  };

  const memoizedTabs = useMemo(
    () =>
      tabs.map(({ id, label, className, isSuccess, isDisabled }) => ({ id, label, className, isSuccess, isDisabled })),
    [tabs]
  );

  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div role="tablist">
      <TabItems
        tabs={memoizedTabs}
        activeTab={activeTab}
        wrapperClassName={tabControlClassName}
        onTabClick={handleTabClick}
        isShowTicked={isShowTicked}
      />
      {tabs.map(({ id, content }) => (
        <div
          role="tabpanel"
          id={`panel-${id}`}
          key={id}
          hidden={activeTab !== id}
          className={activeTab !== id ? "hidden" : "mt-1"}
          tabIndex={0}
          aria-labelledby={`tab-${id}`}
        >
          {content}
        </div>
      ))}
    </div>
  );
};
